import {
    Refresh as RefreshIcon,
    ShowChart,
    TrendingDown,
    TrendingUp,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Skeleton,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { geminiApi } from '../../../services/gemini.js';
import { Impact, impactAnalysisApi, ImpactAnalysisData } from '../../../services/impactAnalysis.js';
import { useWidgetData } from '../hooks/useWidgetData.js';
import { WidgetProps } from '../types/index.js';

export const ImpactAnalysisWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateOutputs,
  onUpdateState,
}) => {
  const [selectedImpact, setSelectedImpact] = useState<Impact | null>(null);
  const [stockAnalysis, setStockAnalysis] = useState<any>(null);

  // Get filter stock from widget inputs if connected
  const filterStockInput = widget.inputs.find(input => input.id === 'filterStock')?.value;
  const filterStock = typeof filterStockInput === 'object' ? filterStockInput?.symbol : filterStockInput;
  // Fallback to configured ticker (derived from profile) if no filter stock is connected yet
  const fallbackTicker = (widget.config as any)?.ticker as string | undefined;
  const effectiveSymbol = (filterStock && typeof filterStock === 'string') ? filterStock : (fallbackTicker || undefined);
  
  // Debug logging
  useEffect(() => {
    console.log('Impact Analysis - filterStockInput:', filterStockInput);
    console.log('Impact Analysis - filterStock:', filterStock);
  }, [filterStockInput, filterStock]);

  // Fetch impact analysis data
  const fetchImpactData = useCallback(async () => {
    const profileId = (widget.config as any)?.profileId as string | undefined;
    // Guard: wait until we have a profileId to avoid fetching generic analysis that mentions SPY/QQQ
    if (!profileId) {
      console.log('[ImpactAnalysisWidget] Skipping fetch (no profileId yet)');
      return {
        impacts: [],
        marketSentiment: 'neutral' as const,
        lastUpdated: new Date().toISOString(),
        summary: 'Loading…'
      } satisfies ImpactAnalysisData;
    }

    // If we have a specific stock (from connection or fallback), prefer stock-specific analysis only
    if (effectiveSymbol && typeof effectiveSymbol === 'string') {
      try {
        // Get price and volume from the connected stock data if available
        const stockPrice = typeof filterStockInput === 'object' ? (filterStockInput.price || 0) : 0;
        const stockVolume = typeof filterStockInput === 'object' ? (filterStockInput.volume || 0) : 0;

        console.log('[ImpactAnalysisWidget] fetchImpactData', {
          profileId,
          effectiveSymbol,
          stockPrice,
          stockVolume,
          hasFilterStockObject: typeof filterStockInput === 'object',
          fallbackTicker,
        });

        const stockData = await geminiApi.analyzeStock(effectiveSymbol, stockPrice, stockVolume, undefined, { profileId });
        setStockAnalysis(stockData);

        // Transform stock analysis into impact format
        const stockImpacts: Impact[] = [
          {
            type: 'stock',
            title: `${effectiveSymbol} Analysis: ${stockData.sentiment.toUpperCase()}`,
            description: stockData.summary,
            confidence: 0.85,
            impact: stockData.sentiment === 'bullish' ? 'high' : stockData.sentiment === 'bearish' ? 'low' : 'medium',
            affectedStocks: [effectiveSymbol]
          },
          ...stockData.keyPoints.slice(0, 3).map((point: string, index: number) => ({
            type: 'stock' as const,
            title: `${effectiveSymbol} Key Factor ${index + 1}`,
            description: point,
            confidence: 0.8,
            impact: 'medium' as const,
            affectedStocks: [effectiveSymbol]
          }))
        ];

        const onlyStock: ImpactAnalysisData = {
          impacts: stockImpacts,
          marketSentiment: 'neutral',
          lastUpdated: new Date().toISOString(),
          summary: `${effectiveSymbol} Analysis: ${stockData.summary}`,
        };
        console.log('[ImpactAnalysisWidget] Stock-only analysis', {
          stockImpacts: stockImpacts.length,
        });
        return onlyStock;
      } catch (error) {
        console.error('Error fetching stock analysis:', error);
        // Fall back to general analysis
        const fallback = await impactAnalysisApi.getImpactAnalysis(profileId, { wid: widget.id, reason: 'fallback' });
        console.log('[ImpactAnalysisWidget] Fallback general analysis only', {
          generalImpacts: fallback?.impacts?.length,
        });
        return fallback;
      }
    }

    // No specific stock, return general analysis
    const data = await impactAnalysisApi.getImpactAnalysis(profileId, { wid: widget.id, reason: 'general-only' });
    console.log('[ImpactAnalysisWidget] General analysis only (no effectiveSymbol)', {
      profileId,
      impacts: data?.impacts?.length,
    });
    setStockAnalysis(null);
    return data;
  }, [effectiveSymbol, filterStockInput, fallbackTicker]);

  // Use the widget data hook
  const { data: analysisData, loading, error, refresh } = useWidgetData<ImpactAnalysisData>({
    widget,
    onUpdateState,
    fetchData: fetchImpactData,
    refreshInterval: 300000, // 5 minutes
    dependencies: [effectiveSymbol, filterStockInput?.price, filterStockInput?.volume, fallbackTicker], // Re-fetch when input or fallback changes
  });

  // All impacts are now relevant when a stock is selected
  const filteredImpacts = useMemo(() => {
    if (!analysisData?.impacts) return [];
    const symbolUpper = (effectiveSymbol || '').toString().toUpperCase();
    const allowIndex = symbolUpper === 'SPY' || symbolUpper === 'QQQ';
    return analysisData.impacts.filter((impact) => {
      // Respect explicit request for SPY/QQQ by input; otherwise hide those index-centric items
      const affected = (impact.affectedStocks || []).map((s) => String(s).toUpperCase());
      const mentionsIndex =
        affected.includes('SPY') ||
        affected.includes('QQQ') ||
        /\b(SPY|QQQ)\b/.test(String(impact.title || '')) ||
        /\b(SPY|QQQ)\b/.test(String(impact.description || ''));
      if (mentionsIndex && !allowIndex) return false;
      return true;
    });
  }, [analysisData?.impacts, effectiveSymbol]);

  // Update outputs when selection changes
  useEffect(() => {
    const outputs: any = {};
    
    if (selectedImpact) {
      outputs.selectedImpact = selectedImpact;
    }
    
    if (filteredImpacts.length > 0) {
      outputs.filteredImpacts = filteredImpacts;
    }

    if (analysisData) {
      outputs.marketSentiment = analysisData.marketSentiment;
      outputs.summary = analysisData.summary;
    }

    // Output the full stock data with sentiment if we have it
    if (filterStockInput && stockAnalysis) {
      outputs.analyzedStock = {
        ...filterStockInput,
        sentiment: stockAnalysis.sentiment,
        summary: stockAnalysis.summary,
        keyPoints: stockAnalysis.keyPoints,
      };
    }

    onUpdateOutputs(outputs);
    try {
      console.log('[ImpactAnalysisWidget] Outputs updated', {
        selectedImpact: Boolean(selectedImpact),
        filteredImpacts: filteredImpacts.length,
        analyzedStock: Boolean(outputs.analyzedStock),
        marketSentiment: analysisData?.marketSentiment,
      });
    } catch {}
  }, [selectedImpact, filteredImpacts.length, analysisData?.marketSentiment, analysisData?.summary, filterStockInput, stockAnalysis]);

  // Handle impact selection
  const handleSelectImpact = (impact: Impact) => {
    setSelectedImpact(impact);
  };

  // Get impact icon based on type
  const getImpactIcon = (type: string) => {
    switch (type) {
      case 'market':
        return <ShowChart />;
      case 'sector':
        return <TrendingUp />;
      case 'stock':
        return <TrendingDown />;
      default:
        return <ShowChart />;
    }
  };

  // Get impact color based on severity
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%',
      background: 'linear-gradient(180deg, rgba(20,20,20,0.6) 0%, rgba(10,10,10,0.6) 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 8,
      backdropFilter: 'blur(8px) saturate(140%)',
    }}>
      {/* Header */}
      <Box sx={{ 
        p: 1, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Impact Analysis
          </Typography>
          {effectiveSymbol && (
            <Chip 
              label={`Analyzing: ${effectiveSymbol}`} 
              size="small" 
              color="primary" 
              variant="filled"
            />
          )}
          {loading && <Typography variant="caption">(Updating...)</Typography>}
        </Box>
        <IconButton size="small" onClick={refresh}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Summary */}
      {analysisData?.summary && (
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {analysisData.summary}
          </Typography>
          {analysisData.marketSentiment && (
            <Chip 
              label={`Market: ${analysisData.marketSentiment}`}
              size="small"
              color={
                analysisData.marketSentiment === 'bullish' ? 'success' :
                analysisData.marketSentiment === 'bearish' ? 'error' : 'default'
              }
              sx={{ ml: 1, mt: 0.5 }}
            />
          )}
        </Box>
      )}

      {/* Impact List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading && filteredImpacts.length === 0 ? (
          <Box sx={{ p: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 1 }}>
            {error}
          </Alert>
        ) : filteredImpacts.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary" variant="body2" align="center">
              {loading 
                ? 'Analyzing market impacts...' 
                : 'No impact data available. Click refresh to load.'}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {filteredImpacts.map((impact, index) => (
              <ListItem
                key={index}
                onClick={() => handleSelectImpact(impact)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: selectedImpact === impact ? 'action.selected' : 'transparent',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={`${impact.type} impact`}>
                        {getImpactIcon(impact.type)}
                      </Tooltip>
                      <Typography variant="subtitle2" sx={{ flex: 1 }}>
                        {impact.title}
                      </Typography>
                      <Chip
                        label={impact.impact}
                        size="small"
                        color={getImpactColor(impact.impact)}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.primary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {impact.description}
                      </Typography>
                      {impact.affectedStocks && impact.affectedStocks.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {impact.affectedStocks.slice(0, 5).map((stock) => (
                            <Chip
                              key={stock}
                              label={stock}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          ))}
                          {impact.affectedStocks.length > 5 && (
                            <Chip
                              label={`+${impact.affectedStocks.length - 5} more`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      )}
                      {impact.confidence && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Confidence: {(impact.confidence * 100).toFixed(0)}%
                          </Typography>
                          {impact.type === 'stock' && stockAnalysis && (
                            <Typography variant="caption" color="primary.main" fontWeight="medium">
                              AI Generated
                            </Typography>
                          )}
                        </Box>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};
